import { useSyncExternalStore } from 'react'

/**
 * Cross-component signal for "the user just submitted their Capital
 * application". Lives in `sessionStorage` (keyed per consolidated practice)
 * so it survives soft navigations within the tab but does not leak across
 * tabs or browser sessions.
 *
 * The page-level component flips its local `applicationSubmitted` state
 * immediately after submit, but the sidebar promo tile is a separate
 * consumer and has no access to that state. Stripe's server-side transition
 * from `delivered` → `accepted` can lag the submit by minutes-to-hours in a
 * real review, so without this signal the tile would keep advertising an
 * offer that has already been acted on.
 *
 * The flag is cleared automatically once the server reports an offer that is no
 * longer in a promo state (see `useStripeCapitalConnect` caller wiring).
 */
const KEY = (consolidatedPracticeId: string): string =>
	`capital_submitted_${consolidatedPracticeId}`

const EVENT = 'capital-application-submitted'

export const markCapitalApplicationSubmitted = (consolidatedPracticeId: string): void => {
	if (typeof window === 'undefined') return
	window.sessionStorage.setItem(KEY(consolidatedPracticeId), '1')
	window.dispatchEvent(new Event(EVENT))
}

export const clearCapitalApplicationSubmitted = (consolidatedPracticeId: string): void => {
	if (typeof window === 'undefined') return
	if (!window.sessionStorage.getItem(KEY(consolidatedPracticeId))) return
	window.sessionStorage.removeItem(KEY(consolidatedPracticeId))
	window.dispatchEvent(new Event(EVENT))
}

const isCapitalApplicationSubmitted = (consolidatedPracticeId: string | undefined): boolean => {
	if (typeof window === 'undefined' || !consolidatedPracticeId) return false
	return window.sessionStorage.getItem(KEY(consolidatedPracticeId)) === '1'
}

const subscribe = (cb: () => void): (() => void) => {
	if (typeof window === 'undefined') return () => {}
	window.addEventListener(EVENT, cb)
	return () => window.removeEventListener(EVENT, cb)
}

/**
 * Reactive read of the per-practice submit flag. Any caller that flips the
 * flag via `markCapitalApplicationSubmitted` will trigger a re-render in
 * every component using this hook.
 */
export const useCapitalApplicationSubmitted = (
	consolidatedPracticeId: string | undefined
): boolean =>
	useSyncExternalStore(
		subscribe,
		() => isCapitalApplicationSubmitted(consolidatedPracticeId),
		() => false
	)
